.container {
  display: grid;
  gap: var(--space-3);
  padding: var(--space-4);
  border: var(--border-default) solid var(--color-border-subtle);
  border-radius: var(--radius-surface);
  background: var(--color-bg-surface);
  color: var(--color-content-primary);
  box-shadow: var(--shadow-sm);
}

.rawContainer {
  border: var(--border-default) solid var(--color-border-subtle);
  background: var(--color-bg-surface);
}

.aiContainer {
  border: 2px dashed var(--color-border-strong);
  background: color-mix(in srgb, var(--color-bg-surface) 95%, var(--color-navy-800));
}

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding-bottom: var(--space-2);
  border-bottom: var(--border-default) solid var(--color-border-subtle);
}

.meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.evidenceId {
  font: var(--type-code);
  font-weight: 600;
  color: var(--color-content-primary);
}

.kindBadge {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-round);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-secondary);
}

.rawBadge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-round);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-primary);
}

.aiBadge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-round);
  background: var(--color-bg-inverse);
  border: var(--border-default) solid var(--color-border-strong);
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-inverse);
}

.hashChip {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-small);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-code);
  font-size: 0.75rem;
  color: var(--color-content-tertiary);
  font-variant-numeric: tabular-nums;
}

.passiveChip {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-small);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-code);
  font-size: 0.75rem;
  color: var(--color-content-secondary);
}

.aiCallout {
  display: grid;
  gap: var(--space-1);
  padding: var(--space-3);
  border-radius: var(--radius-control);
  background: color-mix(in srgb, var(--color-bg-subtle) 90%, var(--color-navy-800));
  border: 1px dashed var(--color-border-default);
}

.aiCalloutHeader {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-content-primary);
}

.aiCalloutTitle {
  font: var(--type-label);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
}

.aiCalloutText {
  margin: 0;
  font: var(--type-compact);
  color: var(--color-content-secondary);
}

.aiReasoning {
  margin: 0;
  font: var(--type-compact);
  color: var(--color-content-primary);
  font-style: italic;
}

.modelMeta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  font: var(--type-label);
  color: var(--color-content-tertiary);
}

.sourceText {
  font: var(--type-compact);
  color: var(--color-content-secondary);
}

.timestamp {
  font: var(--type-label);
  color: var(--color-content-tertiary);
  font-variant-numeric: tabular-nums;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.comparisonGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-3);
}

.comparisonCard {
  display: grid;
  gap: var(--space-1);
  padding: var(--space-3);
  border-radius: var(--radius-control);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
}

.comparisonLabel {
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-secondary);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
}

.comparisonText {
  font: var(--type-body);
  color: var(--color-content-primary);
  margin: 0;
  line-height: 1.4;
}

.codeBlock {
  margin: 0;
  padding: var(--space-2) 0;
  border-radius: var(--radius-control);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-code);
  overflow-x: auto;
  max-height: 380px;
}

.codeBlock:focus-visible {
  outline: var(--border-strong) solid var(--color-focus-ring);
  outline-offset: 2px;
  box-shadow: 0 0 0 2px var(--focus-ring-inner, var(--color-bg-surface));
}

.lineRow {
  display: flex;
  align-items: baseline;
  min-height: 22px;
  padding: 0 var(--space-3);
  line-height: 1.5;
}

.lineRow:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme='dark'] .lineRow:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.lineNumber {
  display: inline-block;
  width: 36px;
  flex-shrink: 0;
  color: var(--color-content-tertiary);
  user-select: none;
  font-variant-numeric: tabular-nums;
  text-align: right;
  margin-right: var(--space-3);
}

.lineContent {
  flex: 1;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Unified diff highlighting (DESIGN_SYSTEM.md §10.6) */
.addition {
  background: var(--color-positive-surface) !important;
  color: var(--color-positive-content);
  border-left: 3px solid var(--color-positive-emphasis);
}

.deletion {
  background: var(--color-negative-surface) !important;
  color: var(--color-negative-content);
  border-left: 3px solid var(--color-negative-emphasis);
}

/* Redacted secret pills */
.redactedPill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  border-radius: var(--radius-small);
  background: var(--color-bg-inverse);
  color: var(--color-content-inverse);
  font: var(--type-code);
  font-size: 0.85em;
  font-weight: 600;
  vertical-align: baseline;
}

.redactedPill[data-unmasked='true'] {
  background: var(--color-bg-selected);
  color: var(--color-content-primary);
  border: var(--border-default) solid var(--color-border-strong);
}

.unmaskBtn {
  all: unset;
  cursor: pointer;
  padding: 0 4px;
  border-radius: var(--radius-none);
  text-decoration: underline;
  font-size: 0.8em;
  opacity: 0.8;
}

.unmaskBtn:hover {
  opacity: 1;
}

.unmaskBtn:focus-visible {
  outline: 1px solid var(--color-focus-ring);
}

.limitations {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-compact);
}

.limitationsHeading {
  font-weight: 600;
  color: var(--color-content-secondary);
}

.limitationsText {
  color: var(--color-content-tertiary);
}
